/**
 * Email Builder Types
 */

import type { EmailBlock } from "./email-blocks";
import type { PaddingConfiguration } from "./block-configurations";

export type EmailDocumentStatus = "Draft" | "Published" | "Archived";

/**
 * Document-level configuration for email shell
 * Mirrors: ApplicationServices.EmailBuilder.EmailDocumentConfiguration
 */
export interface DocumentConfiguration {
  // Layout
  width: number; // Range: 200-1200, Default: 600
  alignment: 'left' | 'center' | 'right'; // Default: 'center'
  
  // Background
  canvasBackgroundColor?: string; // Hex color for the area surrounding the email content (mj-body). Default: undefined (uses editor muted bg)
  backgroundColor?: string; // Hex color for the email content background (mj-wrapper). Default: '#ffffff'
  backgroundImage?: string; // URL
  backgroundRepeat: 'repeat' | 'no-repeat' | 'repeat-x' | 'repeat-y'; // Default: 'no-repeat'
  backgroundPosition: 'top' | 'center' | 'bottom'; // Default: 'center'
  
  // Spacing
  padding: PaddingConfiguration; // Default: all sides 0
  
  // Other
  language?: string; // Max 10 chars, Default: 'en'
}

export interface EmailDocument {
  id: string;
  accountId: string;
  templateId?: string;
  name: string;
  globalStyles: string;
  configuration?: string; // JSON serialized DocumentConfiguration
  blocks: EmailBlock[];
  currentVersion: number;
  versions: EmailDocumentVersion[];
  campaigns: EmailCampaign[];
  status: EmailDocumentStatus;
  renderedMjml?: string;
  renderedHtmlBlobUrl?: string;
  renderedAt?: string;
  createdByUserId: string;
  createdAt: string;
  updatedAt: string;
}

export interface EmailDocumentVersion {
  id: string;
  emailDocumentId: string;
  versionNumber: number;
  blockStructureSnapshot: string; // Serialized block tree
  globalStylesSnapshot: string; // JSON string
  changeDescription?: string;
  changeType: "Manual" | "AutoSave" | "Publish";
  createdByUserId: string;
  createdAt: string;
  renderedMjml?: string;
}

export interface EmailTemplate {
  id: string;
  accountId?: string; // null for system templates
  name: string;
  description?: string;
  previewImageUrl?: string;
  category: string;
  tags: string[];
  blockStructureJson: string; // Serialized block tree
  globalStyles: string; // JSON string
  expectedMergeTags: string[]; // Merge tags used
  isSystemTemplate: boolean;
  isPublic: boolean;
  isActive: boolean;
  usageCount: number;
  createdAt: string;
  updatedAt: string;
}

export interface CustomBlock {
  id: string;
  accountId: string;
  name: string;
  description?: string;
  thumbnailUrl?: string;
  category: string;
  blockStructureJson: string; // Serialized block tree
  mergeTagsUsed: string[]; // Merge tags used
  usageCount: number;
  lastUsedAt?: string;
  isShared: boolean;
  isActive: boolean;
  createdByUserId: string;
  createdAt: string;
}

export enum EmailCampaignType {
  Promotional = "Promotional",
  Transactional = "Transactional",
  Newsletter = "Newsletter",
}

export enum EmailCampaignStatus {
  Draft = "Draft",
  Scheduled = "Scheduled",
  Sending = "Sending",
  Sent = "Sent",
  Paused = "Paused",
  Failed = "Failed",
  Cancelled = "Cancelled",
}

export type ContactListType = "Static" | "Dynamic" | "Segment";

export interface ContactList {
  id: string;
  accountId: string;
  name: string;
  description?: string;
  listType: ContactListType;
  isActive: boolean;
  contactCount: number;
  createdAt: string;
}

export interface EmailCampaign {
  id: string;
  accountId: string;
  emailDocumentId: string;
  name: string;
  type: EmailCampaignType;
  subject: string;
  previewText?: string;
  fromName: string;
  fromEmail: string;
  replyToEmail?: string;
  status: EmailCampaignStatus;
  scheduledSendAt?: string;
  sentAt?: string;
  recipientListJson?: string; // JSON with recipient config
  estimatedRecipients: number;
  actualRecipientsSent: number;
  trackOpens: boolean;
  trackClicks: boolean;
  openCount: number;
  clickCount: number;
  bounceCount: number;
  unsubscribeCount: number;
  parentCampaignId?: string; // For A/B testing
  variantName?: string;
  createdByUserId: string;
  createdAt: string;
  updatedAt: string;
}

export interface PagedResult<T> {
  items: T[];
  totalCount: number;
  page: number;
  pageSize: number;
  totalPages: number;
  hasPreviousPage: boolean;
  hasNextPage: boolean;
}

// Re-export EmailBlock for convenience
export type { EmailBlock } from "./email-blocks";
